import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, message, Row } from 'antd';
import { Modal, Steps, StepsActionStyled, StepsContentStyled } from '../styles';
import { ModalProps } from 'antd/lib/modal';
import { UploadFile } from 'antd/lib/upload/interface';
import CampaignName from './CampaignName';
import CampaignAudience from './Audience/CampaignAudience';
import CampaignMessage from './CampaignMessage';
import CampaignReady from './CampaignReady';
import CampaignSaved from './CampaignSaved';
import SavingCampaign from './SavingCampaign';
import useFormValidation from 'utils/useFormValidation';
import { api, apiV2, dialogApi } from 'services/api';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import type { Auths } from 'interfaces/bitrix24';
import { NumberContext } from 'contexts/legacy/NumberContext';
import { Number, NumberInfo, NumberMeta } from 'interfaces/number';
import CampaignTemplateMessage, { Location } from './CampaignTemplateMessage';
import { Template } from 'interfaces/template';
import axios, { AxiosRequestConfig } from 'axios';
import FileUploadResponse from './interfaces/file-upload-response.type';
import { LanguageContext } from 'contexts/LanguageContext';
import moment from 'moment-timezone';

function CreateMarketingCampaignModal(props: ModalProps) {
	const [auths, setAuths] = useState<Auths>({ access_token: '', refresh_token: '' });
	const [domain, setDomain] = useState<string>();
	const [current, setCurrent] = useState(0);
	const [loading, setLoading] = useState(false);
	const [templateList, setTemplateList] = useState<Template[]>([]);
	const [templateListError, setTemplateListError] = useState<{
		message: string;
		code?: string | number;
		type?: string;
		fbtrace_id?: string;
	} | null>(null);
	const [activeNumbers, setActiveNumbers] = useState<NumberInfo[]>([]);
	const [file, setFile] = useState<UploadFile[]>([]);
	const numberContext = useContext<Number>(NumberContext);
	const bitrix24 = useContext(Bitrix24Context);
	const { dictionary } = useContext(LanguageContext);

	const headers = {
		'Access-Token': auths.access_token,
		'Refresh-Token': auths.refresh_token,
	};
	const fields = {
		campaignName: '',
		campaignWorktime: false,
		campaignOriginNumber: '',
		campaignAudience: '',
		campaignSegment: [] as string[],
		campaignMessage: '',
		campaignSelectedTemplate: null as Template | null,
		campaignSelectedTemplateParams: [] as string[],
		campaignLocationTemplate: null as Location | null,
		campaignWhenToSend: null as number | null,
		campaignScheduledDate: null as moment.Moment | null,
		campaignMessageFiles: null as FileUploadResponse | null,
		campaignNumberMeta: null as NumberMeta | null,
	};
	const rules = {
		campaignName: [{ required: true, message: dictionary.required_campaign_name }],
		campaignWorktime: [{ required: false }],
		campaignOriginNumber: [{ required: true, message: dictionary.required_source_number }],
		campaignAudience: [{ required: true, message: dictionary.required_audience }],
		campaignSegment: [{ required: true, message: dictionary.required_segment }],
		campaignMessage: [{ required: true, message: dictionary.required_message }],
		campaignSelectedTemplate: [{ required: true, message: dictionary.required_template }],
		campaignSelectedTemplateParams: [{ required: true, message: dictionary.required_parameters }],
		campaignLocationTemplate: [{ required: false, message: dictionary.location_required }],
		campaignWhenToSend: [{ required: true, message: dictionary.required_send_when }],
		campaignScheduledDate: [{ required: true, message: dictionary.required_send_date }],
		campaignMessageFiles: [{ required: false }],
		campaignNumberMeta: [{ required: false }],
	};
	const { values, errors, handleChange, validate, resetValidation } = useFormValidation(
		fields,
		rules,
	);
	const steps = [
		dictionary.name,
		dictionary.audience,
		dictionary.message,
		dictionary.send,
		dictionary.finish,
	];

	const isLastStep = current === steps.length - 1;

	const isSelectedNumberTemplate = () => {
		const number = activeNumbers.find((number) => number.number === values.campaignOriginNumber);
		return number?.api === 'dialog' || number?.api === 'messagebird' || number?.api === 'meta';
	};

	const selectedNumber = activeNumbers.find(
		(number) => number.number === values.campaignOriginNumber,
	);

	useEffect(() => {
		setActiveNumbers(numberContext.numbers.actives);
	}, [numberContext.numbers.actives]);

	useEffect(() => {
		if (!values.campaignOriginNumber) return;
		setTemplateList([]);
		setTemplateListError(null);
		const number = activeNumbers.find((number) => number.number === values.campaignOriginNumber);
		if (number?.api === 'meta') {
			getTemplatesListMeta(number.token);
		} else {
			getTemplatesListDialog();
		}
	}, [values.campaignOriginNumber]);

	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		setAuths(bitrix24.auth);
	}, [bitrix24.auth, bitrix24.infos.domain]);

	function prev() {
		return setCurrent(current - 1);
	}

	function next() {
		validate((errors) => {
			if (current === 0 && errors.campaignName) return;

			if (current === 1) {
				const hasSegments = values.campaignSegment.length > 0;
				if (errors.campaignOriginNumber) return;
				if (hasSegments && !isArrayOfEmptyString(errors.campaignSegment as string[])) return;
				if (!hasSegments && errors.campaignAudience) return;
			}

			const isTemplate = isSelectedNumberTemplate();
			if (current === 2) {
				if (!isTemplate && errors.campaignMessage) return;
				if (isTemplate) {
					const hasErrors = !isArrayOfEmptyString(
						errors.campaignSelectedTemplateParams as string[],
					);

					if (errors.campaignSelectedTemplate || hasErrors) return;

					if (values.campaignSelectedTemplate) {
						const headerComponent = values.campaignSelectedTemplate?.components?.find(
							(component) => component.type === 'HEADER',
						);

						if (headerComponent?.format === 'LOCATION' && !values.campaignLocationTemplate) {
							message.error(dictionary.location_required);
							return;
						}
					}
				}
			}

			setCurrent(current + 1);
			resetValidation();
		});
	}

	function isArrayOfEmptyString(strArray: string[]) {
		return strArray.every((item) => item === '');
	}

	async function uploadFile(file: UploadFile[]) {
		const config: AxiosRequestConfig<any> = {
			headers: {
				...headers,
				'Content-Type': 'multipart/form-data',
			},
		};
		const body = new FormData();
		body.append('file', file[0] as any);

		const response = await apiV2.post(`/file/upload/${domain}`, body, config);
		const fileData = response.data as FileUploadResponse;
		handleChange({ target: { name: 'campaignMessageFiles', value: fileData } } as any);
		return fileData;
	}

	function getAudienceNumbersFromString(audience: string) {
		if (!audience) return [];
		const audienceArray = audience.trim().replace(/\s/g, ',').split(',');

		return audienceArray.filter((n) => n !== '');
	}

	async function submitCampaign() {
		validate(async (errors) => {
			setLoading(true);

			if (errors.campaignWhenToSend) return;
			if (values.campaignWhenToSend === 3 && errors.campaignScheduledDate) return;

			const fileData = file.length ? await uploadFile(file) : null;
			const config = { headers };

			const audience = getAudienceNumbersFromString(values.campaignAudience);
			if (!audience) {
				setCurrent(1);
				return;
			}

			if (selectedNumber && selectedNumber.api === 'meta') {
				values.campaignNumberMeta = {
					wabaId: selectedNumber.waba_account_id,
					accountToken: selectedNumber.token,
					phoneNumberId: selectedNumber.device_id,
				};
			}

			const campaignSelectedTemplate = isSelectedNumberTemplate()
				? {
						id: values.campaignSelectedTemplate?.id,
						parameters: [...values.campaignSelectedTemplateParams],
						location: values.campaignLocationTemplate,
				  }
				: undefined;

			const body = {
				name: values.campaignName,
				originNumber: values.campaignOriginNumber,
				audience: audience,
				segments: values.campaignSegment,
				template: campaignSelectedTemplate,
				message: values.campaignMessage,
				file: fileData,
				scheduledDate: getScheduledDate(values.campaignWhenToSend!, values.campaignScheduledDate),
				numberMeta: values.campaignNumberMeta ?? null,
				worktime: values.campaignWorktime,
			};

			return await apiV2.post(`/marketing-campaign/${domain}`, body, config).then(async () => {
				setLoading(false);
				setCurrent(current + 1);
				return true;
			});
		});
	}

	function resetForm() {
		setCurrent(0);
		setLoading(false);
		resetValidation();
		resetValues();
	}

	function resetValues() {
		setFile([]);
		Object.entries(fields).forEach(([key, value]) => {
			handleChange({ target: { name: key, value } } as any);
		});
	}

	function handleChangeFile(files: UploadFile[]) {
		setFile(files);
	}

	function getScheduledDate(whenToSend: number, scheduledDate: moment.Moment | null) {
		switch (whenToSend) {
			case 1:
				return moment().tz('America/Sao_Paulo');
			case 2:
				return null;
			case 3:
				return scheduledDate;
			default:
				return null;
		}
	}

	async function getTemplatesListDialog() {
		const http = selectedNumber?.api === 'dialog' ? dialogApi : api;
		const action =
			selectedNumber?.api === 'dialog' ? 'template/get-template-list' : 'getTemplateList';
		const data = {
			action,
			api: selectedNumber?.api || '',
			sender: values.campaignOriginNumber,
			domain: domain,
			auth: bitrix24.infos.auth,
		};

		await http
			.post(action, data, {
				headers: {
					Domain: domain || '',
					Number: values.campaignOriginNumber,
				},
			})
			.then(async (res) => {
				if (res.data.dados) {
					setTemplateList(res.data.dados);
					if (res.data.dados.errors) {
						console.error(res.data.dados.errors);
					}
				} else {
					setTemplateList([]);
					console.error(res.data.errors);
				}
			})
			.catch((error) => {
				setTemplateList([]);
				console.error('Error fetching template list:', error);
				const errorCode = error.response?.data?.errorCode;
				const errorMessage =
					errorCode && Object.prototype.hasOwnProperty.call(dictionary, errorCode)
						? dictionary[errorCode as keyof typeof dictionary]
						: dictionary.template_error;
				message.error(errorMessage);
			});
	}

	async function getTemplatesListMeta(token: string) {
		const request = {
			params: {
				token,
				domain,
			},
		};

		try {
			const result = await api.get('getTemplates', request);

			if (result.data?.statusCode >= 400 || result.data?.error) {
				const errorData = result.data;
				const errorMessage = errorData.message || dictionary.template_error;

				console.error('getTemplatesListMeta: Error response detected', errorData);

				setTemplateList([]);
				setTemplateListError({
					message: errorMessage,
					code: errorData.error?.code || errorData.statusCode,
					type: errorData.error?.type,
					fbtrace_id: errorData.error?.fbtrace_id,
				});
				return;
			}

			setTemplateList(result.data);
		} catch (error: any) {
			setTemplateList([]);
			console.error('Error fetching template list:', error);
			const errorCode = error.response?.data?.errorCode;
			const errorMessage =
				errorCode && Object.prototype.hasOwnProperty.call(dictionary, errorCode)
					? dictionary[errorCode as keyof typeof dictionary]
					: dictionary.template_error;
			message.error(errorMessage);
		}
	}

	function finishCampaignCreation(e: React.MouseEvent<HTMLElement>) {
		resetForm();
		if (props.onOk) props.onOk(e);
	}

	return (
		<Modal {...props} footer={null} width={1200}>
			<Row className="mb-4">
				<Steps progressDot current={current}>
					{steps.map((item) => (
						<Steps.Step title={item} key={item} />
					))}
				</Steps>
			</Row>
			<Row>
				<StepsContentStyled>
					<Row
						style={{
							minHeight: 300,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<Col span={16}>
							{current === 0 && (
								<CampaignName
									name="campaignName"
									value={values.campaignName}
									onChange={handleChange}
									message={errors.campaignName as string}
								/>
							)}
							{current === 1 && (
								<CampaignAudience
									originNumber={values.campaignOriginNumber}
									originNumberMessage={errors.campaignOriginNumber as string}
									onChangeOriginNumber={(value) =>
										handleChange({ target: { name: 'campaignOriginNumber', value } } as any)
									}
									audience={values.campaignAudience}
									audienceFieldName="campaignAudience"
									audienceMessage={errors.campaignAudience as string}
									onChangeAudience={(value) =>
										handleChange({ target: { name: 'campaignAudience', value } } as any)
									}
									segment={values.campaignSegment}
									segmentMessage={errors.campaignSegment as string[]}
									onChangeSegment={(value) =>
										handleChange({ target: { name: 'campaignSegment', value } } as any)
									}
								/>
							)}
							{current === 2 &&
								(isSelectedNumberTemplate() ? (
									<CampaignTemplateMessage
										isStatic={!!values.campaignAudience}
										selectedTemplate={values.campaignSelectedTemplate}
										params={values.campaignSelectedTemplateParams}
										templateList={templateList}
										templateError={errors.campaignSelectedTemplate as string}
										templateFetchError={templateListError}
										paramsErrors={errors.campaignSelectedTemplateParams as string[]}
										onChangeSelectedTemplate={(template) =>
											handleChange({
												target: { name: 'campaignSelectedTemplate', value: template },
											} as any)
										}
										onChangeParams={(params) =>
											handleChange({
												target: { name: 'campaignSelectedTemplateParams', value: params },
											} as any)
										}
										onChangeLocation={(params) =>
											handleChange({
												target: { name: 'campaignLocationTemplate', value: params },
											} as any)
										}
									/>
								) : (
									<CampaignMessage
										value={values.campaignMessage}
										files={file}
										isStatic={values.campaignAudience}
										onChangeValue={(value: any) =>
											handleChange({ target: { name: 'campaignMessage', value } } as any)
										}
										onChangeFiles={handleChangeFile}
										message={errors.campaignMessage as string}
									/>
								))}
							{current === 3 && (
								<>
									{!loading ? (
										<CampaignReady
											campaignName={values.campaignName}
											campaignWhenToSend={values.campaignWhenToSend}
											campaignScheduledDate={values.campaignScheduledDate}
											campaignWorktime={values.campaignWorktime}
											whenToSendMessage={errors.campaignWhenToSend as string}
											ScheduledDateMessage={errors.campaignScheduledDate as string}
											onChangeWorktime={(value) =>
												handleChange({ target: { name: 'campaignWorktime', value } } as any)
											}
											onChangeWhenToSend={(value) =>
												handleChange({ target: { name: 'campaignWhenToSend', value } } as any)
											}
											onChangeScheduledDate={(date) =>
												handleChange({
													target: { name: 'campaignScheduledDate', value: date },
												} as any)
											}
										/>
									) : (
										<SavingCampaign />
									)}
								</>
							)}
							{current === 4 && <CampaignSaved campaignName={values.campaignName} />}
						</Col>
					</Row>
				</StepsContentStyled>
				<StepsActionStyled>
					<Col
						span={12}
						style={{ display: 'flex', justifyContent: 'start', alignContent: 'center' }}
					>
						{current > 0 && !isLastStep && <Button onClick={prev}>{dictionary.previous}</Button>}
					</Col>
					<Col span={12} style={{ display: 'flex', justifyContent: 'end', alignContent: 'center' }}>
						{current < steps.length - 2 && (
							<Button type="primary" onClick={next}>
								{dictionary.next}
							</Button>
						)}
						{current === steps.length - 2 && (
							<Button
								type="primary"
								onClick={submitCampaign}
								loading={loading}
								disabled={
									!values.campaignWhenToSend ||
									(values.campaignWhenToSend === 3 && !values.campaignScheduledDate)
								}
							>
								{loading ? dictionary.saving : dictionary.save}
							</Button>
						)}
						{isLastStep && (
							<Button onClick={finishCampaignCreation} type="primary">
								{dictionary.finish}
							</Button>
						)}
					</Col>
				</StepsActionStyled>
			</Row>
		</Modal>
	);
}

export default CreateMarketingCampaignModal;
